<template>
  <div>
    <b-card>
      <h5>Filtros</h5>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label-for="quarterSelectOverview">
              <template #label>
                <span class="d-flex align-items-center">
                  Trimestre/Ano
                  <feather-icon
                    id="quarter-info-overview"
                    icon="InfoIcon"
                    size="14"
                    class="ml-1 text-muted cursor-pointer"
                  />
                  <b-tooltip target="quarter-info-overview" triggers="hover" placement="top">
                    T1: Jan – Mar &nbsp;·&nbsp; T2: Abr – Jun &nbsp;·&nbsp; T3: Jul – Set &nbsp;·&nbsp; T4: Out – Dez
                  </b-tooltip>
                </span>
              </template>
              <div class="d-flex">
                <v-select
                  v-model="selectedQuarter"
                  :options="quarterOptions"
                  :clearable="false"
                  :reduce="opt => opt.value"
                  label="label"
                  class="mr-1"
                  style="min-width: 80px; flex: 1"
                  @input="search(1)"
                />
                <v-select
                  v-model="selectedYear"
                  :options="yearOptions"
                  :clearable="false"
                  style="min-width: 88px; flex: 1"
                  @input="search(1)"
                />
              </div>
              <small class="text-muted">{{ quarterMonthsLabel }}</small>
            </b-form-group>
          </b-col>
          <template v-if="structureType != structureTypes.OWN">
            <b-col cols="12" md="4" class="mb-1">
              <b-form-group label="PN" label-for="searchConsultant">
                <v-select
                  id="searchConsultant"
                  multiple
                  v-model="consultant"
                  :reduce="(consultant_list) => consultant_list.id"
                  :options="consultantList"
                  :loading="loading.consultantList"
                  :close-on-select="false"
                  label="name"
                  @keyup.enter="search"
                >
                  <template #no-options>
                    Sem resultados
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
              class="mb-1"
              v-if="structureType == structureTypes.STRUCTURE"
            >
              <b-form-group label="Franqueado" label-for="searchFranchise">
                <v-select
                  id="searchFranchise"
                  multiple
                  v-model="franchise"
                  :reduce="(franchise_list) => franchise_list.id"
                  :options="franchises"
                  :loading="loading.franchises"
                  :close-on-select="false"
                  label="name"
                  @keyup.enter="search"
                >
                  <template #no-options>
                    Sem resultados
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </template>
        </b-row>
        <b-row>
          <b-col cols="12" md="6" class="mt-1 mb-md-0 mb-2">
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(1)"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
          <b-col cols="12" md="6" class="mt-1 mb-md-0 mb-2 text-right">
            <b-button
              variant="outline-primary"
              @click.prevent="search"
              :disabled="loadingSearch"
            >
              <b-spinner v-if="loadingSearch" small />
              <feather-icon v-else icon="SearchIcon" />
              <span>
                {{ loadingSearch ? "Aguarde..." : "Pesquisar" }}
              </span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BSpinner,
  BTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as sharedTypes from "@/modules/shared/store/types";
import * as accountTypes from "@/modules/account/store/types";
import * as types from "../store/types";
import * as structureTypes from "@/constants/structure_options";

const QUARTER_MONTHS = {
  1: "Jan – Mar",
  2: "Abr – Jun",
  3: "Jul – Set",
  4: "Out – Dez",
};

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BFormGroup,
    BButton,
    BSpinner,
    BTooltip,
    vSelect,
  },
  props: {
    quarter: {
      type: Number,
      required: true,
    },
    year: {
      type: Number,
      required: true,
    },
    search: {
      type: Function,
      required: true,
    },
    structureType: {
      type: String,
      required: true,
    },
    loadingSearch: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      structureTypes,
      loading: {
        franchises: false,
        consultantList: false,
      },
      franchise: undefined,
      consultant: undefined,
      selectedYear: this.year,
      selectedQuarter: this.quarter,
      minYear: 2022,
      maxYear: new Date().getFullYear() + 1,
      itemsPerPage: 10,
      quarterOptions: [
        { value: 1, label: "T1" },
        { value: 2, label: "T2" },
        { value: 3, label: "T3" },
        { value: 4, label: "T4" },
      ],
    };
  },
  setup() {
    return { toast: useToast() };
  },
  watch: {
    structureType() {
      this.franchise = undefined;
      this.consultant = undefined;
      this.search(1);
    },
  },
  computed: {
    ...mapGetters({
      franchises: sharedTypes.FRANCHISES,
      consultants: sharedTypes.CONSULTANTS,
      consultantDirectTeam: sharedTypes.CONSULTANT_DIRECT_TEAM,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
      user: accountTypes.USER,
    }),
    yearOptions() {
      const years = [];
      for (let y = this.minYear; y <= this.maxYear; y++) years.push(y);
      return years;
    },
    quarterMonthsLabel() {
      return QUARTER_MONTHS[this.selectedQuarter] || "";
    },
    consultantList() {
      if (this.structureType == structureTypes.STRUCTURE) {
        return this.consultants;
      } else {
        return this.consultantDirectTeam;
      }
    },
  },
  mounted() {
    this.selectedYear = this.year;
    this.selectedQuarter = this.quarter;
    this.itemsPerPage = this.initialItemsPerPage;
    this.search(1);

    this.loading.franchises = true;
    this.getFranchises()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar as franquias para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.franchises = false;
      });

    this.loading.consultantList = true;
    this.getConsultants()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os parceiros de negócio para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.consultantList = false;
      });

    this.loading.consultantList = true;
    this.getConsultantDirectTeam(this.user.appify_consultant_id)
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os parceiros de negócio para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.consultantList = false;
      });

    this.getRangeDates()
      .then((resp) => {
        if (resp && resp.data) {
          this.minYear = resp.data.initial_year || 2022;
          this.maxYear = resp.data.end_year || new Date().getFullYear();
        }
      })
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar o intervalo de datas. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  },
  methods: {
    ...mapActions({
      getFranchises: sharedTypes.GET_FRANCHISES,
      getConsultants: sharedTypes.GET_CONSULTANTS,
      getConsultantDirectTeam: sharedTypes.GET_CONSULTANT_DIRECT_TEAM,
      getRangeDates: types.GET_RANGE_DATES,
    }),
  },
};
</script>

<style lang="scss">
.form-group {
  margin-bottom: 0;
}
</style>
